export const isLotActiveForInteraction = (stage: unknown): boolean => {
  if (typeof stage !== 'string') return true

  const normalizedStage = stage.trim()
  if (!normalizedStage) return true

  return normalizedStage === 'Active'
}

/**
 * Option-list label for a lot in a picker: the key alone for an Active
 * (interactive) lot, and `key (Stage)` for a non-Active one so the disabled
 * option explains why it can't be chosen. Keeps a redundant "(Active)" — noise
 * on the common case that reads as clutter — out of the UI, and matches how the
 * Biomass sampling picker already labels lots.
 */
export const formatLotPickerLabel = (lot: { lotKey?: unknown; stage?: unknown }): string => {
  const key = String(lot.lotKey ?? '').trim() || 'Lot'
  if (isLotActiveForInteraction(lot.stage)) return key
  const stage = String(lot.stage ?? '').trim()
  return stage ? `${key} (${stage})` : key
}

export const filterInteractiveLots = <T extends { stage?: unknown }>(lots: T[]): T[] => {
  return lots.filter((lot) => isLotActiveForInteraction(lot.stage))
}

/**
 * T3-D-04a: map a 5-value `LotStage` (`Active | Inactive | Harvested |
 * Planned | Dormant`) to the 2-value snapshot stored on a production row.
 *
 * Following the same convention as `isLotActiveForInteraction`, missing or
 * empty inputs are treated as `'Active'` (the default when a lot has not
 * been explicitly moved out of the Active phase). Every non-Active
 * `LotStage` value collapses to `'Inactive'` for row-cache purposes; the
 * canonical lifecycle stays on the Lot record itself.
 */
export const toProductionRowStage = (stage: unknown): 'Active' | 'Inactive' => {
  if (typeof stage !== 'string') return 'Active'
  const trimmed = stage.trim()
  if (!trimmed) return 'Active'
  return trimmed === 'Active' ? 'Active' : 'Inactive'
}